import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const impactItems = [
  {
    icon: "ri-graduation-cap-line",
    value: "2,500+",
    labelKey: "impact.students",
    descKey: "impact.students.desc",
  },
  {
    icon: "ri-drop-line",
    value: "18,000+",
    labelKey: "impact.water",
    descKey: "impact.water.desc",
  },
  {
    icon: "ri-stethoscope-line",
    value: "12,400+",
    labelKey: "impact.medical",
    descKey: "impact.medical.desc",
  },
  {
    icon: "ri-home-heart-line",
    value: "480+",
    labelKey: "impact.families",
    descKey: "impact.families.desc",
  },
];

export default function ImpactSection() {
  const { t } = useTranslation();
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );

    if (headerRef.current) {
      headerRef.current.classList.add("reveal");
      observer.observe(headerRef.current);
    }

    if (gridRef.current) {
      const children = Array.from(gridRef.current.children) as HTMLElement[];
      children.forEach((child, i) => {
        child.classList.add("reveal-child");
        child.style.transitionDelay = `${i * 120}ms`;
      });
      observer.observe(gridRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 bg-[#1B2838] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-[#4ECDC4] -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#4ECDC4] translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14" ref={headerRef}>
          <span className="text-xs font-semibold uppercase tracking-widest text-[#4ECDC4] mb-3 block">
            {t("impact.tag")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("impact.title")}
          </h2>
          <p className="text-gray-400 text-base max-w-xl mx-auto">{t("impact.subtitle")}</p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          ref={gridRef}
        >
          {impactItems.map((item) => (
            <div
              key={item.labelKey}
              className="bg-white/5 border border-white/10 rounded-xl p-6 text-center hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#4ECDC4]/20 text-[#4ECDC4] mx-auto mb-4">
                <i className={`${item.icon} text-2xl`}></i>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{item.value}</div>
              <div className="text-[#4ECDC4] font-semibold text-sm mb-3">{t(item.labelKey)}</div>
              <p className="text-gray-400 text-xs leading-relaxed">{t(item.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
